import { type SignInEmailSchema, signInEmailSchema } from '@coas/validators/payload';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslations } from 'next-intl';
import type { ZodErrorMap } from 'zod';
import { AuthButton } from '~/entities/AuthButton';
import { AuthInput } from '~/entities/AuthInput';
import { useSignInMutation } from '../model/mutations';

export const EmailForm = () => {
    const t = useTranslations();
    const { mutate, isPending } = useSignInMutation();

    const errorMap: ZodErrorMap = useMemo(
        () => (issue, ctx) => {
            if (issue.code === 'too_small' && (issue.minimum as number) === 1) {
                return { message: t('auth.errors.emailRequired') };
            }
            if (issue.code === 'invalid_string' && (issue as any).validation === 'email') {
                return { message: t('auth.errors.emailFormat') };
            }
            return { message: ctx.defaultError };
        },
        [t],
    );

    const { control, handleSubmit } = useForm<SignInEmailSchema>({
        resolver: zodResolver(signInEmailSchema, { errorMap }),
        defaultValues: { provider: 'email', data: { email: '' } },
        mode: 'onTouched',
    });

    const onSubmit = useCallback((values: SignInEmailSchema) => mutate(values), [mutate]);

    return (
        <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-4'>
            <AuthInput name='data.email' control={control} label={t('auth.emailLabel')} disabled={isPending} />
            <AuthButton type='submit' isLoading={isPending}>
                {t('auth.signInButton')}
            </AuthButton>
        </form>
    );
};
